import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, Plus, Trash2, Copy, Save, AlertTriangle, ExternalLink, CheckCircle2 } from 'lucide-react';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

const today = () => new Date().toISOString().slice(0, 10);

export default function FeesInvoiceCreatePage() {
  const orgId = useOrgId();
  const { selectedYearId } = useAcademicYear();
  const { t } = useT();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [form, setForm] = useState({ class_id: '', section_id: null, student_id: '', invoice_date: today(), due_date: '', status: 'unpaid' });
  const [groupFilter, setGroupFilter] = useState('');
  const [lines, setLines] = useState([]);
  const [groupSeparate, setGroupSeparate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dupWarn, setDupWarn] = useState(null);       // single-student duplicate {message, duplicates}
  const [bulkCheck, setBulkCheck] = useState(null);   // whole-class pre-check {body, already, to_create, ...}
  const [bulkResult, setBulkResult] = useState(null); // whole-class result {created, skipped, skipped_names}

  const loadRefs = useCallback(async () => {
    if (!orgId) return;
    const [cls, grp] = await Promise.all([schoolApi.listClasses(orgId, selectedYearId), schoolApi.listFeeGroups(orgId)]);
    setClasses(cls); setGroups(grp);
  }, [orgId, selectedYearId]);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const onClass = async (class_id) => {
    setForm((f) => ({ ...f, class_id, student_id: '' }));
    // Use the invoice-specific roster so the dropdown count matches exactly what the
    // whole-class generator will target (active enrollments for the year, assigned to a
    // section) and agrees with the Classes & Sections roster count.
    const res = await schoolApi.classInvoiceRoster(orgId, class_id, selectedYearId);
    setStudents(res.students || []);
  };

  const onGroupFilter = async (gid) => {
    setGroupFilter(gid);
    const params = { fee_group_id: gid };
    if (form.class_id) params.class_id = form.class_id;
    const types = await schoolApi.listFeeTypes(orgId, params);
    setFeeTypes(types);
    // append fee types of this group as invoice lines (skip already-added)
    setLines((prev) => {
      const have = new Set(prev.map((l) => l.fee_type_id));
      const add = types.filter((ty) => !have.has(ty.id)).map((ty) => ({
        fee_type_id: ty.id, name: ty.name, due_date: ty.due_date || '', fee_group_id: gid,
        amount: Number(ty.default_amount) || 0, waiver: 0, paid: 0,
      }));
      return [...prev, ...add];
    });
  };

  const setLine = (i, key, val) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, [key]: val } : l));
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));
  const cloneAmount = () => {
    if (!lines.length) return;
    const amt = Number(lines[0].amount) || 0;
    setLines((prev) => prev.map((l) => ({ ...l, amount: amt })));
    toast.success('Amount cloned to all lines');
  };

  const num = (v) => Number(v) || 0;
  const totals = lines.reduce((a, l) => {
    const sub = num(l.amount) - num(l.waiver);
    a.amount += num(l.amount); a.waiver += num(l.waiver); a.sub += sub; a.paid += num(l.paid); return a;
  }, { amount: 0, waiver: 0, sub: 0, paid: 0 });

  const save = async () => {
    if (!form.class_id) { toast.error('Select a class'); return; }
    if (!form.student_id) { toast.error('Select a student (or All Students)'); return; }
    if (!lines.length) { toast.error('Add at least one fee line'); return; }
    const payloadLines = lines.map((l) => ({ fee_type_id: l.fee_type_id, name: l.name, due_date: l.due_date || null, amount: num(l.amount), waiver: num(l.waiver), paid: num(l.paid), fee_group_id: l.fee_group_id }));

    if (form.student_id === 'ALL') {
      const body = { organization_id: orgId, academic_year_id: selectedYearId, class_id: form.class_id, section_id: form.section_id, invoice_date: form.invoice_date, due_date: form.due_date || null, status: form.status, lines: payloadLines, group_separate: groupSeparate };
      setSaving(true);
      try {
        const prev = await schoolApi.bulkInvoicePreview(body);
        setBulkCheck({ body, ...prev });
      } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to check'); }
      finally { setSaving(false); }
      return;
    }

    const body = { organization_id: orgId, academic_year_id: selectedYearId, class_id: form.class_id, section_id: form.section_id, student_id: form.student_id, invoice_date: form.invoice_date, due_date: form.due_date || null, status: form.status, lines: payloadLines };
    setSaving(true);
    try {
      await schoolApi.createFeeInvoice(body);
      toast.success('Invoice created');
      navigate('/school/fees/invoices');
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && typeof d === 'object' && d.error === 'duplicate_invoice') {
        setDupWarn(d);
      } else {
        toast.error((typeof d === 'string' ? d : d?.message) || 'Failed to save');
      }
    } finally { setSaving(false); }
  };

  const confirmBulk = async () => {
    if (!bulkCheck) return;
    setSaving(true);
    try {
      const res = await schoolApi.bulkCreateInvoices(bulkCheck.body);
      setBulkCheck(null);
      setBulkResult(res);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to generate'); }
    finally { setSaving(false); }
  };

  const viewExisting = (invNumber) => navigate(`/school/fees/invoices?search=${encodeURIComponent(invNumber)}`);

  return (
    <div className="space-y-6" data-testid="fees-invoice-create-page">
      <PageHeader icon={Receipt} title="New Fees Invoice" subtitle="Create a fees invoice for a student or a whole class" />
      {!selectedYearId && <Card className="p-3 text-sm text-amber-600">Select an academic year (top bar) first.</Card>}
      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div><Label>Class *</Label>
            <Select value={form.class_id} onValueChange={onClass}><SelectTrigger data-testid="inv-class"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Student *</Label>
            <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })} disabled={!form.class_id}>
              <SelectTrigger data-testid="inv-student"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="ALL">All Students ({students.length})</SelectItem>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label>Invoice date</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="inv-date" /></div>
          <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
          <div><Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="inv-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent></Select></div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label>Fees Group filter (load fee types)</Label>
            <Select value={groupFilter} onValueChange={onGroupFilter}><SelectTrigger className="w-64" data-testid="inv-group-filter"><SelectValue placeholder="Select a group" /></SelectTrigger>
              <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent></Select></div>
          <Button variant="outline" onClick={cloneAmount} disabled={!lines.length} data-testid="inv-clone-amount"><Copy className="w-4 h-4 mr-1" /> Clone Amount</Button>
          <label className="flex items-center gap-2 text-sm cursor-pointer border rounded-md px-3 py-2">
            <input type="checkbox" checked={groupSeparate} onChange={(e) => setGroupSeparate(e.target.checked)} data-testid="inv-group-separate" />
            <span>Group Fees - Generate Separate Invoice (one per fee type)</span>
          </label>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="p-3">Fee type</th><th className="p-3">Due date</th><th className="p-3 text-right">Amount</th><th className="p-3 text-right">Waiver</th><th className="p-3 text-right">Sub total</th><th className="p-3 text-right">Paid</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {lines.length === 0 ? <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Select a Fees Group above to load fee types</td></tr> :
              lines.map((l, i) => (
                <tr key={i} className="border-b" data-testid={`inv-line-${i}`}>
                  <td className="p-2">{l.name}</td>
                  <td className="p-2">{l.due_date || '\u2014'}</td>
                  <td className="p-2"><Input type="number" className="w-28 text-right ml-auto" value={l.amount} onChange={(e) => setLine(i, 'amount', e.target.value)} data-testid={`inv-amount-${i}`} /></td>
                  <td className="p-2"><Input type="number" className="w-24 text-right ml-auto" value={l.waiver} onChange={(e) => setLine(i, 'waiver', e.target.value)} data-testid={`inv-waiver-${i}`} /></td>
                  <td className="p-2 text-right font-medium">{(num(l.amount) - num(l.waiver)).toLocaleString()}</td>
                  <td className="p-2"><Input type="number" className="w-24 text-right ml-auto" value={l.paid} onChange={(e) => setLine(i, 'paid', e.target.value)} /></td>
                  <td className="p-2 text-right"><Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => removeLine(i)}><Trash2 className="w-4 h-4" /></Button></td>
                </tr>
              ))}
          </tbody>
          {lines.length > 0 && <tfoot><tr className="border-t-2 font-semibold bg-muted/40" data-testid="inv-totals">
            <td className="p-3" colSpan={2}>Totals</td>
            <td className="p-3 text-right">{totals.amount.toLocaleString()}</td>
            <td className="p-3 text-right">{totals.waiver.toLocaleString()}</td>
            <td className="p-3 text-right">{totals.sub.toLocaleString()}</td>
            <td className="p-3 text-right">{totals.paid.toLocaleString()}</td>
            <td></td>
          </tr></tfoot>}
        </table>
      </Card>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || !lines.length} data-testid="inv-save"><Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save Invoice'}</Button>
      </div>

      {/* Single-student duplicate warning */}
      <Dialog open={!!dupWarn} onOpenChange={(o) => { if (!o) setDupWarn(null); }}>
        <DialogContent data-testid="dup-warning-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-600"><AlertTriangle className="w-5 h-5" /> Duplicate invoice</DialogTitle></DialogHeader>
          <p className="text-sm" data-testid="dup-warning-message">{dupWarn?.message}</p>
          <div className="space-y-1.5 mt-2">
            {(dupWarn?.duplicates || []).map((d) => (
              <div key={d.fee_group_id} className="flex items-center justify-between border rounded-md px-3 py-2 text-sm">
                <span><span className="font-medium">{d.fee_group_name}</span> <span className="text-muted-foreground">{'\u2192'}</span> <span className="font-mono">{d.invoice_number}</span></span>
                <Button size="sm" variant="outline" onClick={() => viewExisting(d.invoice_number)} data-testid={`dup-view-${d.invoice_number}`}><ExternalLink className="w-3.5 h-3.5 mr-1" /> View invoice</Button>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">Different fee groups can be invoiced separately for the same student. Remove the duplicated group's lines to save the rest.</p>
          <DialogFooter><Button variant="outline" onClick={() => setDupWarn(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Whole-class pre-check summary */}
      <Dialog open={!!bulkCheck} onOpenChange={(o) => { if (!o) setBulkCheck(null); }}>
        <DialogContent className="max-w-lg" data-testid="bulk-check-dialog">
          <DialogHeader><DialogTitle>Generate invoices for class</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <p>{bulkCheck?.fee_group_names?.length ? <>Fee group(s): <span className="font-medium">{bulkCheck.fee_group_names.join(', ')}</span></> : null}</p>
            <p className="text-xs text-muted-foreground" data-testid="bulk-total-line">
              <span className="font-medium text-foreground">{bulkCheck?.student_count ?? 0}</span> enrolled student(s) in this class.
              Every enrolled student is accounted for below — none are silently excluded.
            </p>
            <div className="flex gap-3">
              <div className="flex-1 border rounded-md p-3 text-center"><div className="text-2xl font-semibold text-emerald-600" data-testid="bulk-create-count">{bulkCheck?.create_count ?? 0}</div><div className="text-xs text-muted-foreground">will get a new invoice</div></div>
              <div className="flex-1 border rounded-md p-3 text-center"><div className="text-2xl font-semibold text-amber-600" data-testid="bulk-skip-count">{bulkCheck?.skip_count ?? 0}</div><div className="text-xs text-muted-foreground">skipped (see reasons)</div></div>
            </div>
            {bulkCheck?.already?.length > 0 && (
              <div className="max-h-48 overflow-auto border rounded-md divide-y" data-testid="bulk-already-list">
                {bulkCheck.already.map((a) => (
                  <div key={a.student_id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
                    <span className="truncate">{a.student_name}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-muted-foreground">{a.reason || 'Already invoiced'}</span>
                      <span className="font-mono text-muted-foreground">{(a.invoice_numbers || []).join(', ')}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkCheck(null)}>Cancel</Button>
            <Button onClick={confirmBulk} disabled={saving || (bulkCheck?.create_count ?? 0) === 0} data-testid="bulk-confirm">{saving ? 'Generating...' : `Generate ${bulkCheck?.create_count ?? 0} invoice(s)`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Whole-class result */}
      <Dialog open={!!bulkResult} onOpenChange={(o) => { if (!o) { setBulkResult(null); navigate('/school/fees/invoices'); } }}>
        <DialogContent data-testid="bulk-result-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-emerald-600"><CheckCircle2 className="w-5 h-5" /> Generation complete</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <p><span className="font-semibold" data-testid="bulk-result-created">{bulkResult?.created ?? 0}</span> invoice(s) created for {bulkResult?.created_students ?? 0} student(s).</p>
            <p><span className="font-semibold" data-testid="bulk-result-skipped">{bulkResult?.skipped ?? 0}</span> student(s) skipped (already had an invoice for this fee group).</p>
            {bulkResult?.skipped_names?.length > 0 && (
              <div className="max-h-40 overflow-auto border rounded-md px-3 py-2 text-xs text-muted-foreground">{bulkResult.skipped_names.join(', ')}</div>
            )}
          </div>
          <DialogFooter><Button onClick={() => { setBulkResult(null); navigate('/school/fees/invoices'); }} data-testid="bulk-result-close">Go to invoices</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
